import { createFileRoute } from "@tanstack/react-router";

import {
  ServiceIntentPage,
  type ServiceFaq,
  type ServiceSection,
} from "@/components/seo/ServiceIntentPage";
import { servicePageHead } from "@/lib/service-page-seo";

const PATH = "/meta-pixel-conversions-api";
const TITLE = "Meta Pixel & Conversions API Setup Service | Adkiro";
const DESCRIPTION =
  "Meta Pixel and Conversions API setup, audit and troubleshooting: event mapping, server and browser deduplication, values, currencies and measurement verification.";

const SECTIONS: ServiceSection[] = [
  {
    eyebrow: "Event design",
    title: "Track the business actions that actually matter",
    copy: "A larger event list is not automatically better. We map the customer journey and define the events, parameters and priorities that support advertising decisions without presenting implementation as legal advice.",
    points: [
      "Map standard and permitted custom events to meaningful user actions",
      "Check domains, dataset ownership and access before implementation",
      "Verify values, currencies, content identifiers and essential parameters",
      "Document consent and data-handling dependencies for the client's legal review",
    ],
  },
  {
    eyebrow: "Browser and server",
    title: "Configure Pixel and CAPI to work together",
    copy: "Browser and server events should complement one another. When both report the same action, consistent event names and event IDs allow Meta to deduplicate instead of counting two conversions.",
    points: [
      "Review Pixel, partner integration, gateway or server-side implementation options",
      "Align event names, timestamps and event IDs across browser and server sources",
      "Test deduplication and investigate duplicate or missing events",
      "Review available customer-information parameters and data-processing controls",
    ],
  },
  {
    eyebrow: "Verification",
    title: "Test the implementation before using it to scale",
    copy: "A green event indicator is not enough. We compare expected customer actions with test events, diagnostics and the client's source of truth, then document known gaps.",
    points: [
      "Use controlled test journeys for the relevant conversion actions",
      "Review diagnostics, event match quality and recent event activity",
      "Reconcile lead counts with your CRM or enquiry inbox",
      "Provide a written implementation summary and unresolved dependencies",
    ],
  },
];

const STEPS = [
  {
    title: "Audit the current signal",
    copy: "We inspect event sources, ownership, integrations, diagnostics and the business actions that should be measured.",
  },
  {
    title: "Implement the agreed design",
    copy: "Browser and server events are configured through the most appropriate supported integration for the client's stack.",
  },
  {
    title: "Verify and document",
    copy: "Test journeys, deduplication and critical parameters are checked before the setup is treated as decision-ready.",
  },
];

const FAQS: ServiceFaq[] = [
  {
    q: "What is the difference between Meta Pixel and Conversions API?",
    a: "Meta Pixel sends browser-side events. Conversions API sends events from a server or supported integration. They can work together when the same events use consistent names and IDs so duplicate reports are deduplicated.",
  },
  {
    q: "Does Conversions API replace the Pixel?",
    a: "Not in every implementation. Many businesses use browser and server events together because each source has different strengths and limitations. The appropriate design depends on the website, platform, consent requirements and available data.",
  },
  {
    q: "Will CAPI guarantee better campaign performance?",
    a: "No. A reliable implementation can improve measurement resilience and the signal available to Meta, but it cannot fix a weak offer, poor creative, site problems or insufficient conversion volume, and it does not guarantee lower costs.",
  },
  {
    q: "How much does a tracking and CAPI setup cost?",
    a: "Tracking is checked and set up as part of a Property Launch or monthly management when the campaign needs it. A standalone Pixel and Conversions API setup is quoted in writing after the free review, before any work begins.",
  },
];

export const Route = createFileRoute("/meta-pixel-conversions-api")({
  head: () =>
    servicePageHead({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Meta Pixel and Conversions API Setup",
      serviceType: "Meta Pixel and Conversions API measurement implementation",
      audience: ["Property agencies", "Property developers", "Brokers"],
      faqs: FAQS,
    }),
  component: MetaTrackingPage,
});

function MetaTrackingPage() {
  return (
    <ServiceIntentPage
      badge="Meta measurement and event quality"
      title="Meta Pixel and Conversions API setup you can verify"
      intro="Event mapping, browser and server implementation, deduplication and testing designed to make Meta reporting more dependable for business decisions."
      qualification="Adkiro handles advertising measurement implementation, not legal advice. Your organization remains responsible for consent, privacy notices and lawful data processing in every market."
      sections={SECTIONS}
      steps={STEPS}
      faqs={FAQS}
      ctaTitle="Find the gaps in your Meta measurement"
      ctaCopy="We will review your event sources, diagnostics and conversion journey, then scope only the implementation work your stack requires."
    />
  );
}
